import React, { useState, useMemo } from 'react';
import {
  Drawer, Box, Typography, TextField, InputAdornment, IconButton,
  Accordion, AccordionSummary, AccordionDetails, Button, Chip
} from '@mui/material';
import {
  Close, Search, ExpandMore, PlayArrow, Replay,
  RocketLaunch, Construction, Settings, Memory, Assignment, Description, Hub, AccountTree, AutoAwesome,
  VerifiedUser, Assessment
} from '@mui/icons-material';
import { helpSections } from './helpArticles';
import { useTour } from './TourProvider';
import { useOnboarding } from '../../context/OnboardingContext';

const iconMap = {
  RocketLaunch: <RocketLaunch />,
  Construction: <Construction />,
  Settings: <Settings />,
  Memory: <Memory />,
  Assignment: <Assignment />,
  Description: <Description />,
  Hub: <Hub />,
  AccountTree: <AccountTree />,
  AutoAwesome: <AutoAwesome />,
  VerifiedUser: <VerifiedUser />,
  Assessment: <Assessment />,
};

const HelpCenter = ({ open, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSection, setExpandedSection] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const { startTour, isTourCompleted } = useTour();
  const onboarding = useOnboarding();

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return helpSections;
    const q = searchQuery.toLowerCase();
    return helpSections
      .map(section => ({
        ...section,
        articles: section.articles.filter(a =>
          a.title.toLowerCase().includes(q) || a.content.toLowerCase().includes(q)
        ),
      }))
      .filter(section => section.articles.length > 0);
  }, [searchQuery]);

  const handleStartTour = (tourId) => {
    onClose();
    setTimeout(() => startTour(tourId), 300);
  };

  const renderArticleContent = (content) => {
    // Simple markdown-like rendering
    return content.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <Box key={i} sx={{ height: 8 }} />;
      // Bold
      const formatted = trimmed.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
      // List items
      if (trimmed.startsWith('- ')) {
        return (
          <Typography key={i} variant="body2" color="text.secondary" sx={{ pl: 2, mb: 0.5, lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: `\u2022 ${formatted.slice(2)}` }} />
        );
      }
      // Numbered items
      const numMatch = trimmed.match(/^(\d+)\.\s/);
      if (numMatch) {
        return (
          <Typography key={i} variant="body2" color="text.secondary" sx={{ pl: 2, mb: 0.5, lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: formatted }} />
        );
      }
      return (
        <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.6 }}
          dangerouslySetInnerHTML={{ __html: formatted }} />
      );
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 420,
          bgcolor: 'background.default',
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 2,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
          Help Center
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </Box>

      {/* Search */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search help articles..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setSelectedArticle(null); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Restart Tutorial */}
      {onboarding && (
        <Box sx={{
          px: 2.5, py: 1.5,
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: 'rgba(102, 126, 234, 0.04)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Getting Started Tutorial
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {onboarding.allComplete
                  ? 'All steps completed'
                  : `${onboarding.completedCount} of ${onboarding.totalSteps} steps done`}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Replay sx={{ fontSize: 16 }} />}
              onClick={() => {
                if (onboarding.allComplete) {
                  // All steps were completed — restart from beginning
                  onboarding.restartTutorial();
                } else {
                  // Tutorial was skipped partway — reopen from where they left off
                  onboarding.reopen();
                }
                onClose();
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                borderColor: '#667eea',
                color: '#667eea',
                '&:hover': { borderColor: '#764ba2', bgcolor: 'rgba(102, 126, 234, 0.08)' },
              }}
            >
              {onboarding.allComplete ? 'Restart' : 'Resume'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {selectedArticle ? (
          /* Article Detail View */
          <Box sx={{ p: 2.5 }}>
            <Button size="small" onClick={() => setSelectedArticle(null)}
              sx={{ mb: 2, textTransform: 'none', color: '#667eea' }}>
              Back to articles
            </Button>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {selectedArticle.title}
            </Typography>
            {selectedArticle.tourId && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PlayArrow />}
                onClick={() => handleStartTour(selectedArticle.tourId)}
                sx={{ mb: 2, textTransform: 'none', borderColor: '#667eea', color: '#667eea' }}
              >
                Start Interactive Tour
                {isTourCompleted(selectedArticle.tourId) && (
                  <Chip label="Completed" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                )}
              </Button>
            )}
            {renderArticleContent(selectedArticle.content)}
          </Box>
        ) : (
          /* Section List */
          filteredSections.map((section) => (
            <Accordion
              key={section.id}
              expanded={expandedSection === section.id}
              onChange={(_, expanded) => setExpandedSection(expanded ? section.id : null)}
              disableGutters
              elevation={0}
              sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ color: '#667eea' }}>
                    {iconMap[section.icon] || <Description />}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {section.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {section.articles.length} article{section.articles.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
                {section.articles.map((article, i) => (
                  <Box
                    key={i}
                    onClick={() => setSelectedArticle(article)}
                    sx={{
                      p: 1.5, mb: 0.5, borderRadius: 1, cursor: 'pointer',
                      border: '1px solid', borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.light' },
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {article.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {article.tourId && (
                        <Chip label="Tour" size="small" variant="outlined"
                          sx={{ height: 20, fontSize: '0.6rem', borderColor: '#667eea', color: '#667eea' }} />
                      )}
                    </Box>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))
        )}

        {filteredSections.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <Typography variant="body2">No articles match "{searchQuery}"</Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default HelpCenter;
